import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Check, Calendar, FileText, Eye, Printer, Smartphone } from 'lucide-react';
export function ProductShowcase() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);
  const items = ['Plantilla mensual de hábitos (núcleo del sistema)', 'Planificación semanal para ordenar la ejecución', 'Planificación diaria para bajar a acciones concretas', 'Sistema visual de seguimiento', 'Formato imprimible y reutilizable', 'Uso físico o digital'];
  useEffect(() => {
    const handleScroll = () => {
      const viewportCenter = window.innerHeight / 2;
      let closestIndex = null;
      let closestDistance = Infinity;
      itemRefs.current.forEach((ref, index) => {
        if (!ref) return;
        const rect = ref.getBoundingClientRect();
        const itemCenter = rect.top + rect.height / 2;
        const distance = Math.abs(itemCenter - viewportCenter);
        if (distance < closestDistance && distance < 100) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      setActiveIndex(closestIndex);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const features = [{
    icon: <Calendar className="w-5 h-5" data-id="element-185" />,
    text: 'Plantilla mensual de hábitos (el núcleo del sistema)'
  }, {
    icon: <FileText className="w-5 h-5" data-id="element-186" />,
    text: 'Planificación semanal para organizar la ejecución'
  }, {
    icon: <FileText className="w-5 h-5" data-id="element-187" />,
    text: 'Planificación diaria para bajar a acciones concretas'
  }, {
    icon: <Eye className="w-5 h-5" data-id="element-188" />,
    text: 'Sistema visual de seguimiento (ves si cumpliste o no)'
  }, {
    icon: <Printer className="w-5 h-5" data-id="element-189" />,
    text: 'Formato imprimible y reutilizable'
  }, {
    icon: <Smartphone className="w-5 h-5" data-id="element-190" />,
    text: 'Compatible con impresión y uso digital'
  }];
  return <section className="py-12 md:py-16 bg-[#0A0A0A] relative overflow-hidden" data-id="element-191">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10" data-id="element-192">
        {/* Title */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center mb-4" data-id="element-193">
          <h2 className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight leading-tight" data-id="element-194">
            Un{' '}
            <span className="text-[#FF6B35]" style={{
            textShadow: '0 0 20px rgba(255, 107, 53, 0.4)'
          }} data-id="element-195">
              sistema visual
            </span>{' '}
            que sí vas a seguir.
          </h2>
        </motion.div>

        {/* Subtitle */}
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="text-center text-gray-400 text-sm md:text-base mb-8" data-id="element-196">
          Diseñado para ejecutar, incluso cuando no tenés ganas.
        </motion.p>

        {/* Main Product Image - Floating without container */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="relative mb-6 flex justify-center" data-id="element-197">
          <img src="https://cdn.magicpatterns.com/uploads/fDQRbeR2ib893Gubdpq43r/6c451dd2-6596-4544-8cd6-c4a432432671.png" alt="Sistema visual de planificación - Plantillas semanales y diarias" className="w-full max-w-3xl h-auto" data-id="element-198" />
        </motion.div>

        {/* Reinforcement Text */}
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.4
      }} className="text-center text-gray-500 text-sm md:text-base mb-12 max-w-2xl mx-auto px-8" data-id="element-199">
          Todo está pensado para sacarte el peso de la cabeza y llevarlo a un
          sistema claro y visible.
        </motion.p>

        {/* What's Included Section */}
        <div className="max-w-xl mx-auto px-8" data-id="element-200">
          <motion.h3 initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }} className="text-sm font-medium text-gray-500 mb-6 uppercase tracking-wide" data-id="element-201">
            ¿Qué incluye el sistema?
          </motion.h3>

          <div className="relative pl-6" data-id="element-202">
            {/* Barra vertical neón naranja */}
            <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-[#FF6B35]/20 to-transparent" data-id="element-203" />

            <ul className="space-y-3" data-id="element-204">
              {items.map((item, index) => <li key={index} ref={el => itemRefs.current[index] = el} className="text-sm relative transition-colors duration-200" style={{
              color: activeIndex === index ? 'rgb(255, 107, 53)' : 'rgb(156, 163, 175)'
            }} data-id="element-205">
                  {/* Punto de activación en la barra */}
                  <div className="absolute -left-[25px] top-1/2 -translate-y-1/2 w-[6px] h-[6px] rounded-full transition-all duration-200" style={{
                backgroundColor: activeIndex === index ? 'rgb(255, 107, 53)' : 'transparent',
                opacity: activeIndex === index ? 1 : 0,
                transform: activeIndex === index ? 'translateY(-50%) scale(1)' : 'translateY(-50%) scale(0.5)',
                boxShadow: activeIndex === index ? '0 0 8px rgba(255, 107, 53, 0.6)' : 'none'
              }} data-id="element-206" />
                  {item}
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}